import { useEffect, useState } from 'react';
import { Grid2 as Grid, Card, CardContent, Typography, Box, List, ListItem, ListItemText } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BarChart } from '@mui/x-charts/BarChart';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import LoadingScreen from '../components/common/LoadingScreen';
import StatusChip from '../components/common/StatusChip';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { formatDate, formatNumber, getErrorMessage } from '../utils/format';

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);
  const [usageSummary, setUsageSummary] = useState({ total: 0, errors: 0, byDevice: [] });
  const [recentLogs, setRecentLogs] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        let partnerList = [];
        if (isAdmin) {
          const res = await partnerService.list({ limit: 100 });
          partnerList = res.partners || [];
        } else if (user?.partnerId) {
          const p = await partnerService.get(user.partnerId);
          partnerList = [p];
        }
        setPartners(partnerList);

        const target = partnerList[0];
        if (target?.id) {
          const usage = await partnerService.getUsage(target.id, { days: 14, limit: 10 });
          setUsageSummary({
            total: usage.stats?.totals?.total || 0,
            errors: usage.stats?.totals?.errors || 0,
            byDevice: usage.stats?.byDevice || [],
          });
          setRecentLogs(usage.recent || []);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin, user?.partnerId]);

  if (loading) return <LoadingScreen />;

  const activePartners = partners.filter(p => p.status === 'active').length;
  const chartData = usageSummary.byDevice.slice(0, 6);
  const chartLabels = chartData.map(d => d._id?.replace(/_/g, ' ') || 'Unknown');
  const chartValues = chartData.map(d => d.count);

  return (
    <>
      <PageHeader
        title={`Welcome, ${user?.firstName || 'Admin'}`}
        subtitle="Platform overview and recent activity"
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {isAdmin && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Partners"
              value={formatNumber(partners.length)}
              icon={<BusinessIcon />}
              color="primary.main"
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={isAdmin ? 'Active Partners' : 'Account Status'}
            value={isAdmin ? formatNumber(activePartners) : user?.status || '—'}
            icon={<TrendingUpIcon />}
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="API Calls (14d)"
            value={formatNumber(usageSummary.total)}
            icon={<VpnKeyIcon />}
            color="secondary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Parse Errors"
            value={formatNumber(usageSummary.errors)}
            icon={<ErrorOutlineIcon />}
            color="error.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage by Device (14 days)
              </Typography>
              {chartValues.length ? (
                <BarChart
                  height={280}
                  series={[{ data: chartValues, label: 'Requests', color: '#1565c0' }]}
                  xAxis={[{ scaleType: 'band', data: chartLabels }]}
                  margin={{ left: 50, right: 20, top: 20, bottom: 60 }}
                />
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No usage data yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List dense disablePadding>
                {recentLogs.length === 0 && (
                  <Typography color="text.secondary" variant="body2">
                    No recent API calls
                  </Typography>
                )}
                {recentLogs.slice(0, 8).map(log => (
                  <ListItem key={log._id || log.id} disableGutters divider>
                    <ListItemText
                      primary={log.deviceType?.replace(/_/g, ' ') || log.endpoint}
                      secondary={formatDate(log.createdAt)}
                    />
                    <StatusChip status={log.success ? 'active' : 'past_due'} label={log.success ? 'OK' : 'Error'} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
